import * as React from 'react';

import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import VisibilityOff from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOn from '@material-ui/icons/VisibilityOutlined';
import { Form, FormikProps, withFormik } from 'formik';
import { withSnackbar, withSnackbarProps } from 'notistack';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../../../actions/account.actions';
import { accountConstants } from '../../../constants/account.constants';
import { validate } from '../../../helpers/validation';
import { accountRoutesConst as routes } from '../../../routes/account.routes';
import LinkButton from '../../../ui/Button/LinkButton';
import { IntroButton } from '../Common/IntroButton';
import AuthLayout from '../Common/Layout';
import LoginTitle from './LoginTitle';

interface IFormValues {
  email: string;
  password: string;
}

const styles = (theme: Theme) =>
  createStyles({
    formFields: {
      // remove chrome autofill color
      '& input:-webkit-autofill': {
        boxShadow: '0 0 0 30px white inset !important',
      },
    },
    textField: {
      // prevent jumping screen
      minHeight: '68px',
    },
    languageBarNoMargin: {
      margin: 0,
    },
    extras: {
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
      },
    },
    hideIcon: {
      color: 'rgba(0, 0, 0, 0.58)',
    },
    linkBtn: {
      textTransform: 'none',
    },
  });

const Title = () => (
  <Translate>
    {(translate) => (
      <LoginTitle
        titlePrefix={translate('Please_enter_your') as string}
        title={(translate('Password') as string).toLowerCase()}
      />
    )}
  </Translate>
);

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = () => {
  return (state: AppState) => {
    const { account } = state;
    return {
      account,
      translate: getTranslate(state.locale),
    };
  };
};

type Props = WithStyles<typeof styles> &
  withSnackbarProps &
  MappedState & { dispatch: ThunkDispatch };
const LoginPassword = compose<Props, {}>(
  setDisplayName('LoginPassword'),
  withStyles(styles, {
    name: 'FdLoginPassword',
    withTheme: true,
  }),
  withSnackbar,
  connect(mapStateToPropsFactory),
  withFormik<Props, IFormValues>({
    displayName: 'LoginPasswordForm',
    mapPropsToValues: (props) => {
      return {
        email: props.account.Email as string,
        password: '',
      };
    },
    handleSubmit: async (values, { props, setSubmitting, setFieldError }) => {
      const { dispatch } = props;
      try {
        await dispatch(accountActions.login(values.email, values.password));
      } catch (error) {
        setFieldError('password', props.translate(error.message) as string);
        props.enqueueSnackbar(props.translate(error.message), {
          variant: 'error',
        });
        setSubmitting(false);
      }
    },
    validateOnBlur: false,
    validate: (values) => {
      return validate.required(values.password);
    },
  })
)(
  class extends React.Component<Props & FormikProps<IFormValues>, { showPassword: boolean }> {
    private inputRef = React.createRef<HTMLInputElement>();

    public constructor(props) {
      super(props);
      this.state = {
        showPassword: false,
      };
    }

    public componentDidMount() {
      this.focus();
    }

    private setShowPassword = () => {
      this.setState({ showPassword: !this.state.showPassword });
    };

    private showPasswordIcon = () => {
      return (
        <InputAdornment
          className={this.props.classes.hideIcon}
          onClick={() => this.setShowPassword()}
          position="end"
        >
          {this.state.showPassword ? <VisibilityOn /> : <VisibilityOff />}
        </InputAdornment>
      );
    };

    public render() {
      const {
        classes,
        translate,
        isValid,
        isSubmitting,
        touched,
        errors,
        handleBlur,
        handleChange,
        account,
        values,
      } = this.props;

      if (!account.Email) {
        return <Redirect to={routes.Login} />;
      }

      return (
        <AuthLayout title={Title}>
          <Grid container alignItems="stretch" direction="column" wrap="nowrap" spacing={2}>
            <Grid item>
              <Form>
                <Grid container spacing={0} className={classes.formFields}>
                  <Grid item xs={12} lg={11}>
                    <TextField
                      name="email"
                      label={translate('Email')}
                      placeholder={translate('Email') as string}
                      type="email"
                      autoComplete="email"
                      fullWidth
                      inputProps={{
                        'data-fd': 'email',
                      }}
                      className={classes.textField}
                      disabled={true}
                      value={values.email}
                    />
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <TextField
                      inputRef={this.inputRef}
                      name="password"
                      label={translate('Password')}
                      placeholder={translate('Password') as string}
                      type={this.state.showPassword ? 'text' : 'password'}
                      autoComplete="off"
                      fullWidth
                      inputProps={{
                        'data-fd': 'password',
                      }}
                      InputProps={{
                        endAdornment: this.showPasswordIcon(),
                      }}
                      className={classes.textField}
                      error={touched.password && !!errors.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.password && errors.password}
                    />
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <IntroButton
                      resource={accountConstants.LOGIN}
                      loading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                      data-fd="next-button"
                    >
                      <Translate id="Login" />
                    </IntroButton>
                  </Grid>
                </Grid>
              </Form>
            </Grid>

            <Grid item container direction="column" className={classes.extras}>
              <Grid item xs={12}>
                <LinkButton
                  color="primary"
                  to={routes.ForgotPassword}
                  type="button"
                  disabled={isSubmitting}
                  fdKey="forgot-password"
                  variant="text"
                  className={classes.linkBtn}
                >
                  <Translate id="Forgot_password" />
                </LinkButton>
              </Grid>
            </Grid>
          </Grid>
        </AuthLayout>
      );
    }

    public focus = () => {
      if (this.inputRef.current) {
        this.inputRef.current.focus();
      }
    };
  }
);

export default LoginPassword;
